import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, Edit2, Trash2, Check, CheckSquare, RefreshCw, Eye,
  AlertTriangle, Shield, XCircle, Phone, Clock, HelpCircle, Zap, Save, X, Filter
} from 'lucide-react';
import api from '../../services/api';

const STATUS_TYPES = [
  { value: 'DELIVERED', label: 'Delivered', color: '#059669', bg: '#ecfdf5', icon: Check },
  { value: 'UNDELIVERED', label: 'Undelivered', color: '#d97706', bg: '#fffbeb', icon: AlertTriangle },
  { value: 'REJECTED', label: 'Rejected', color: '#dc2626', bg: '#fef2f2', icon: XCircle },
  { value: 'NDNC', label: 'NDNC/DND', color: '#7c3aed', bg: '#f5f3ff', icon: Phone },
  { value: 'PENDING', label: 'Pending', color: '#2563eb', bg: '#eff6ff', icon: Clock },
  { value: 'UNKNOWN', label: 'Unknown', color: '#6b7280', bg: '#f9fafb', icon: HelpCircle },
];

const getStatusStyle = (type) => STATUS_TYPES.find(s => s.value === type) || STATUS_TYPES[5];

const SMPPErrorCodeMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0 });
  const [smppNames, setSmppNames] = useState([]);
  const [systemTypes, setSystemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSmpp, setFilterSmpp] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState({
    system_error_code_type: 'DELIVERED',
    system_error_code: '',
    system_error_message: '',
    smpp_name: '',
    smpp_error_code: '',
    smpp_error_code_description: '',
    smpp_error_message: '',
    is_active: 0,
  });
  const [connectors, setConnectors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total_records: 0, total_pages: 0, has_next: false, has_prev: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchMappings = useCallback(async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterSmpp) params.set('smpp_name', filterSmpp);
      if (filterType) params.set('system_error_code_type', filterType);
      if (filterActive) params.set('active_only', filterActive);
      params.set('page', String(page));
      params.set('page_size', String(size));
      const res = await api.get(`/admin/smpp-error-codes/?${params.toString()}`);
      setMappings(res.data.mappings || []);
      setStats(res.data.stats || { total: 0, active: 0, draft: 0 });
      setSmppNames(res.data.smpp_names || []);
      setSystemTypes(res.data.system_types || []);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
        setCurrentPage(res.data.pagination.page);
      }
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
    }
    setLoading(false);
  }, [search, filterSmpp, filterType, filterActive, currentPage, pageSize]);

  const fetchConnectors = useCallback(async () => {
    try {
      const res = await api.get('/admin/smpp-error-codes/connectors');
      setConnectors(res.data.connectors || []);
    } catch (err) {
      console.error('Failed to fetch connectors:', err);
    }
  }, []);

  useEffect(() => { fetchMappings(1, pageSize); }, [search, filterSmpp, filterType, filterActive, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);

  const handleCreate = async () => {
    if (!newRow.smpp_name || !newRow.smpp_error_code || !newRow.system_error_code) return;
    setActionLoading(true);
    try {
      await api.post('/admin/smpp-error-codes/', newRow);
      setShowAddForm(false);
      setNewRow({ system_error_code_type: 'DELIVERED', system_error_code: '', system_error_message: '', smpp_name: '', smpp_error_code: '', smpp_error_code_description: '', smpp_error_message: '', is_active: 0 });
      fetchMappings();
    } catch (err) {
      alert(err.response?.data?.error || 'Create failed');
    }
    setActionLoading(false);
  };

  const handleUpdate = async (id) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/smpp-error-codes/${id}`, editData);
      setEditRow(null);
      fetchMappings();
    } catch (err) {
      alert(err.response?.data?.error || 'Update failed');
    }
    setActionLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await api.delete(`/admin/smpp-error-codes/${id}`);
      fetchMappings();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/admin/smpp-error-codes/${id}/approve`);
      fetchMappings();
    } catch (err) {
      alert('Approve failed');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      await api.post('/admin/smpp-error-codes/bulk-approve', { ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      fetchMappings();
    } catch (err) {
      alert('Bulk approve failed');
    }
    setActionLoading(false);
  };

  const handleAutoPopulate = async (smppName) => {
    if (!smppName) return;
    setActionLoading(true);
    try {
      const res = await api.post('/admin/smpp-error-codes/auto-populate', { smpp_name: smppName });
      alert(`Auto-populated: ${res.data.inserted} inserted, ${res.data.skipped} skipped`);
      fetchMappings();
    } catch (err) {
      alert(err.response?.data?.error || 'Auto-populate failed');
    }
    setActionLoading(false);
  };

  const startEdit = (m) => {
    setEditRow(m.id);
    setEditData({ ...m });
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === mappings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(mappings.map(m => m.id)));
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    setCurrentPage(page);
    fetchMappings(page, pageSize);
  };

  const changePageSize = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchMappings(1, newSize);
  };

  const getPageNumbers = () => {
    const pages = [];
    const total = pagination.total_pages;
    const current = pagination.page;
    const delta = 2;
    let start = Math.max(1, current - delta);
    let end = Math.min(total, current + delta);
    if (current - delta < 1) end = Math.min(total, end + (delta - current + 1));
    if (current + delta > total) start = Math.max(1, start - (current + delta - total));
    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) { if (end < total - 1) pages.push('...'); pages.push(total); }
    return pages;
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-600" /> SMPP Error Code Mapping
          </h1>
          <p className="text-sm text-gray-500 mt-1">Map SMPP operator error codes to system delivery statuses. Review and approve mappings before they take effect.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
            <Plus className="w-4 h-4" /> Add Mapping
          </button>
          <button onClick={fetchMappings}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Mappings', value: stats.total, color: '#6366f1', bg: '#eef2ff' },
          { label: 'Active (Approved)', value: stats.active, color: '#059669', bg: '#ecfdf5' },
          { label: 'Draft (Pending)', value: stats.draft, color: '#d97706', bg: '#fffbeb' },
          { label: 'SMPP Accounts', value: smppNames.length, color: '#2563eb', bg: '#eff6ff' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{s.label}</div>
            <div className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Auto-Populate Section */}
      {connectors.length > 0 && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">Auto-populate standard SMPP error codes for a connector:</span>
          </div>
          <div className="flex items-center gap-2">
            {connectors.map(c => (
              <button key={c.id} onClick={() => handleAutoPopulate(c.name)} disabled={actionLoading}
                className="px-3 py-1.5 bg-white text-indigo-700 text-sm font-medium rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors disabled:opacity-50">
                {c.name} ({c.system_id})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500"><Filter className="w-4 h-4" /> Filters:</div>
          <div className="relative flex-1 min-w-[200px] max-w-[350px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search codes, messages..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" />
          </div>
          <select value={filterSmpp} onChange={e => setFilterSmpp(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 outline-none bg-white">
            <option value="">All SMPP Accounts</option>
            {smppNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 outline-none bg-white">
            <option value="">All Status Types</option>
            {systemTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 outline-none bg-white">
            <option value="">All Status</option>
            <option value="1">Active Only</option>
            <option value="0">Draft Only</option>
          </select>
          {(search || filterSmpp || filterType || filterActive) && (
            <button onClick={() => { setSearch(''); setFilterSmpp(''); setFilterType(''); setFilterActive(''); }}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-amber-800">{selectedIds.size} mapping(s) selected</span>
          <div className="flex gap-2">
            <button onClick={handleBulkApprove} disabled={actionLoading}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
              <CheckSquare className="w-4 h-4" /> Approve Selected
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl p-5 border-2 border-indigo-200 shadow-sm space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Plus className="w-4 h-4 text-indigo-600" /> New Error Code Mapping
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">System Status Type *</label>
              <select value={newRow.system_error_code_type} onChange={e => setNewRow({ ...newRow, system_error_code_type: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200 bg-white">
                {STATUS_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">System Error Code *</label>
              <input type="text" value={newRow.system_error_code} onChange={e => setNewRow({ ...newRow, system_error_code: e.target.value })}
                placeholder="e.g. 000" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">System Error Message *</label>
              <input type="text" value={newRow.system_error_message} onChange={e => setNewRow({ ...newRow, system_error_message: e.target.value })}
                placeholder="e.g. Message Delivered Successfully" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SMPP Account *</label>
              <select value={newRow.smpp_name} onChange={e => setNewRow({ ...newRow, smpp_name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200 bg-white">
                <option value="">Select...</option>
                {connectors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SMPP Error Code *</label>
              <input type="text" value={newRow.smpp_error_code} onChange={e => setNewRow({ ...newRow, smpp_error_code: e.target.value })}
                placeholder="e.g. DELIVRD" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SMPP Code Description</label>
              <input type="text" value={newRow.smpp_error_code_description} onChange={e => setNewRow({ ...newRow, smpp_error_code_description: e.target.value })}
                placeholder="e.g. ESME_ROK" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SMPP Error Message</label>
              <input type="text" value={newRow.smpp_error_message} onChange={e => setNewRow({ ...newRow, smpp_error_message: e.target.value })}
                placeholder="e.g. Delivered" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button onClick={handleCreate} disabled={actionLoading || !newRow.smpp_name || !newRow.smpp_error_code || !newRow.system_error_code}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> Save as Draft
            </button>
            <button onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-3 text-left">
                  <input type="checkbox" checked={selectedIds.size === mappings.length && mappings.length > 0}
                    onChange={toggleSelectAll} className="rounded border-gray-300" />
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status Type</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sys Code</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">System Message</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">SMPP Account</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">SMPP Code</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">SMPP Description</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">SMPP Message</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">State</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
              ) : mappings.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">No mappings found</td></tr>
              ) : mappings.map(m => {
                const st = getStatusStyle(m.system_error_code_type);
                const isEditing = editRow === m.id;
                const Icon = st.icon;

                return (
                  <tr key={m.id} className={`hover:bg-gray-50/50 transition-colors ${m.is_active === 0 ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)} className="rounded border-gray-300" />
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <select value={editData.system_error_code_type} onChange={e => setEditData({ ...editData, system_error_code_type: e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded bg-white">
                          {STATUS_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: st.bg, color: st.color }}>
                          <Icon className="w-3 h-3" /> {m.system_error_code_type}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <input type="text" value={editData.system_error_code} onChange={e => setEditData({ ...editData, system_error_code: e.target.value })}
                          className="w-16 px-2 py-1 text-xs border rounded" />
                      ) : (
                        <span className="font-mono text-xs font-bold text-gray-800">{m.system_error_code}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[180px]">
                      {isEditing ? (
                        <input type="text" value={editData.system_error_message} onChange={e => setEditData({ ...editData, system_error_message: e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded" />
                      ) : (
                        <span className="text-xs text-gray-700 truncate block">{m.system_error_message}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <select value={editData.smpp_name} onChange={e => setEditData({ ...editData, smpp_name: e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded bg-white">
                          {connectors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{m.smpp_name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <input type="text" value={editData.smpp_error_code} onChange={e => setEditData({ ...editData, smpp_error_code: e.target.value })}
                          className="w-20 px-2 py-1 text-xs border rounded" />
                      ) : (
                        <span className="font-mono text-xs font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{m.smpp_error_code}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[160px]">
                      {isEditing ? (
                        <input type="text" value={editData.smpp_error_code_description} onChange={e => setEditData({ ...editData, smpp_error_code_description: e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded" />
                      ) : (
                        <span className="text-xs text-gray-500 truncate block">{m.smpp_error_code_description || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[140px]">
                      {isEditing ? (
                        <input type="text" value={editData.smpp_error_message} onChange={e => setEditData({ ...editData, smpp_error_message: e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded" />
                      ) : (
                        <span className="text-xs text-gray-500 truncate block">{m.smpp_error_message || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {m.is_active === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          <Clock className="w-3 h-3" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isEditing ? (
                          <>
                            <button onClick={() => handleUpdate(m.id)} disabled={actionLoading}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Save">
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setEditRow(null)}
                              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors" title="Cancel">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setViewDetail(m)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View Details">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => startEdit(m)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            {m.is_active === 0 && (
                              <button onClick={() => handleApprove(m.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Approve">
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => handleDelete(m.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Detail Popup */}
      {viewDetail && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Error Code Details</h3>
                  <p className="text-xs text-gray-500">Full mapping information</p>
                </div>
              </div>
              <button onClick={() => setViewDetail(null)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                {(() => {
                  const st = getStatusStyle(viewDetail.system_error_code_type);
                  const StIcon = st.icon;
                  return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold" style={{ background: st.bg, color: st.color }}>
                      <StIcon className="w-4 h-4" />
                      {viewDetail.system_error_code_type}
                    </span>
                  );
                })()}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${viewDetail.is_active === 1 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  {viewDetail.is_active === 1 ? 'Active' : 'Draft'}
                </span>
              </div>
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'System Error Code', value: viewDetail.system_error_code, mono: true },
                  { label: 'SMPP Error Code', value: viewDetail.smpp_error_code, mono: true, bold: true },
                  { label: 'SMPP Name', value: viewDetail.smpp_name },
                  { label: 'Status Type', value: viewDetail.system_error_code_type, bold: true },
                ].map((item, i) => (
                  <div key={i} className="rounded-lg p-2.5" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{item.label}</div>
                    <div className={`text-sm text-gray-800 break-all ${item.mono ? 'font-mono text-xs' : ''} ${item.bold ? 'font-bold' : 'font-medium'}`}>
                      {item.value || '—'}
                    </div>
                  </div>
                ))}
              </div>
              {/* Descriptions */}
              {[
                { label: 'System Error Message', value: viewDetail.system_error_message },
                { label: 'SMPP Error Code Description', value: viewDetail.smpp_error_code_description },
                { label: 'SMPP Error Message', value: viewDetail.smpp_error_message },
              ].map((item, i) => (
                <div key={i}>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{item.label}</div>
                  <div className="p-3 rounded-xl text-sm text-gray-800 whitespace-pre-wrap break-all leading-relaxed" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)', minHeight: '40px' }}>
                    {item.value || '—'}
                  </div>
                </div>
              ))}
              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-2.5" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Created At</div>
                  <div className="text-xs text-gray-600">{viewDetail.created_at ? new Date(viewDetail.created_at).toLocaleString() : '—'}</div>
                </div>
                <div className="rounded-lg p-2.5" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Updated At</div>
                  <div className="text-xs text-gray-600">{viewDetail.updated_at ? new Date(viewDetail.updated_at).toLocaleString() : '—'}</div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className="flex justify-end px-6 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)', background: 'rgba(99, 102, 241, 0.02)' }}>
              <button onClick={() => setViewDetail(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:shadow-md"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select value={pageSize} onChange={e => changePageSize(Number(e.target.value))}
                className="px-2 py-1 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-200 outline-none">
                {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>per page</span>
              <span className="text-gray-400 mx-2">|</span>
              <span className="font-medium text-gray-700">
                {((pagination.page - 1) * pagination.page_size) + 1}–{Math.min(pagination.page * pagination.page_size, pagination.total_records)} of {pagination.total_records}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => goToPage(1)} disabled={!pagination.has_prev}
                className="px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                First
              </button>
              <button onClick={() => goToPage(pagination.page - 1)} disabled={!pagination.has_prev}
                className="px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Prev
              </button>
              {getPageNumbers().map((p, i) => (
                p === '...' ? (
                  <span key={`dots-${i}`} className="px-2 py-1.5 text-xs text-gray-400">...</span>
                ) : (
                  <button key={p} onClick={() => goToPage(p)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      p === pagination.page
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}>
                    {p}
                  </button>
                )
              ))}
              <button onClick={() => goToPage(pagination.page + 1)} disabled={!pagination.has_next}
                className="px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next
              </button>
              <button onClick={() => goToPage(pagination.total_pages)} disabled={!pagination.has_next}
                className="px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">System Status Types</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STATUS_TYPES.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.value} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: s.bg }}>
                <Icon className="w-4 h-4" style={{ color: s.color }} />
                <div>
                  <div className="text-xs font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[10px] text-gray-500">{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-gray-400 space-y-1">
          <p><strong>Delivered</strong> = Successfully delivered to handset (0, 00, 000, DELIVRD)</p>
          <p><strong>Undelivered</strong> = Unable to deliver to handset - subscriber/network error (chargeable)</p>
          <p><strong>Rejected</strong> = Rejected by DLT/operator - entity, template, header, consent errors (chargeable)</p>
          <p><strong>NDNC</strong> = DND/NDNC blocked mobile number from operator</p>
          <p><strong>Pending</strong> = Message submitted, awaiting delivery report</p>
        </div>
      </div>
    </div>
  );
};

export default SMPPErrorCodeMapping;
